/**
 * The photo tray: pictures handed over that are not yet anybody's.
 *
 * A gardener with a garden already growing dumps photos here, and is asked
 * pile by pile what each one is. Answering makes the plant, with the photos
 * on it. Nothing in the tray is a plant until they say so. A photo of seed
 * packets is read for its packets instead (by the server, in the same /tray/guess request), and filed as a
 * plant for each packet (confirmPackets, with its trayIds). A photo of
 * several kinds of plant (a tray of seedlings, a bouquet, a harvest, a mixed
 * bed) is filed as a plant for each kind (fileKinds), unless the gardener
 * says it is one plant (the server's POST /tray/one-plant; the app has no
 * caller for it now). A plant Grow does not have is filed
 * as the gardener's own (TrayNewPlant), for Grow to review.
 *
 * The piles are the server's (audit batch 6a, A33 and A34; owner R13,
 * 2026-10-18): GET /tray answers the photos and the piles it makes of them,
 * each with its guess, its day, its "same as an earlier pile?" offer and its
 * pick (what Grow's guess amounts to on the card). The gardener's moves,
 * removals are posted (moveInTray, removeFromTray),
 * kept on the server so a reload or a second phone shows the same piles, and
 * each answers with the tray. A removed photo is not drawn at all (B45); the
 * server keeps it until the Grow setting photos.REMOVED_KEEP_HOURS runs out.
 *
 * Endpoints (every answer also carries `success`):
 * - GET  /tray?lang= → Tray ({ photos, piles, filed? }); `filed` (B160) is the photos Grow filed onto their plants by itself lately.
 * - POST /tray/move?lang= { moves: [{ trayId, to }] } → Tray.
 * - POST /tray/remove?lang= { trayIds } → Tray and changed. (The server's /tray/restore, its 24-hour undo, is not used by the app.)
 * - POST /tray (multipart: image, takenAt?, locationId?) → { photo }.
 * - POST /tray/guess?tz= { trayIds } → { photos, filed? } (paid AI; see below); `filed` (B160) is the photos Grow was sure of and filed.
 * - POST /tray/sort { trayIds, plantId | newPlant | plantObjId, locationId | newBedName,
 *   stageId, varietyId? | newVariety? } → SortResult. A photo with no date
 *   is filed on the garden's own day, the server's (architecture audit A31:
 *   no day is sent).
 * - POST /tray/kinds { trayId, locationId | newBedName, kinds, group, upToRoom? } → PacketResult
 *   (a kind may carry its own locationId or newBedName, or be { cropId, typeUnsure: true }; the answer's beds lists
 *   the beds used; a bed made from the photo takes it as its cover, D78 P4).
 * - POST /tray/one-plant, /tray/packets?tz= (paid AI), /tray/not-packets
 *   { trayId } → { photo }.
 * - PUT  /tray/:trayId/image ImageEdit and /tray/:trayId/image/reset → { photo }.
 *
 * The guesses are the photo help: over its allowance they are refused with
 * 429 AI_LIMIT (AI_LIMIT_MONTH_PHOTO, _DAY or _TRIAL, read by
 * features/billing/lib/aiLimit), and aiDateParams sends the time zone that
 * refusal's date is worked out in. Filing on Free past its plants is 402
 * FREE_PLANT_LIMIT unless upToRoom.
 *
 * Who uses it: features/photo-tray (useTray, PhotoTray, TrayUploader,
 * GooglePhotosResume, KindsPile, PileCard). Caching: TRAY_QUERY_KEY (['tray'],
 * TRAY_STALE_MS), invalidated after each upload, guess and filing; filing
 * also invalidates 'plants', 'locations' and 'plant-outlook'. Auth: the signed-in token, in the garden being
 * tended.
 *
 * @module tray
 */

import { apiClient } from './apiClient';
import { aiDateParams } from './aiAllowance';
import { getLanguage } from '@/shared/i18n/i18nSetup';
import { trackEvent } from '@/shared/lib/analytics';
import { DateTime } from '../utils/dateUtils';
import type { PacketResult, PacketScan } from './packets';
import type { PlantSuggestion } from './plantNames';
import type { ImageEdit } from '../lib/imageEdit';
import { PhotoCopySkipped, isCopyAnswer, copyFields, readCopyPlace, type CopyOptions } from '../lib/photoCopiesCore';

/**
 * A plant Grow does not have, as the guess saw it: its name, and the plant
 * it grows most like, whose group it is filed in as the gardener's own plant.
 */
export interface TrayNewPlant {
  /** Its common name, lower case: "tomatillo". */
  name: string;
  /** The catalogue plant it grows most like. */
  likePlantId: number;
  /** That plant's group, which it is filed in. */
  groupId: number | null;
  /** The group's name ("Tomato Group"). */
  groupName: string | null;
}

/** What kind of photo the guess saw: one kind of plant, or several in a tray, a bouquet, a harvest or a bed. */
export type TrayGroup = 'one' | 'seedlings' | 'bouquet' | 'harvest' | 'bed';

/** One kind of plant the guess saw in a photo of several: a tray of seedlings, a bouquet, a harvest, a mixed bed. */
export interface TrayKind {
  /** What it is in plain words: "zinnia". */
  seen: string;
  /** The same, plural: "zinnias". */
  plural: string;
  /** The plant it is filed under: its own, or a broad parent (Tender Annual Flower); null for a plant Grow does not have (new). */
  plant_id: number | null;
  /**
   * The variety it is filed as: a plant tag's ("Cherokee Purple"), or under a
   * broad parent the flower's own name ("Zinnia"); null for the plant as Grow has it.
   */
  variety: string | null;
  /** The words on its plant tag, as read; null or absent for none. */
  label?: string | null;
  /** A plant Grow does not have, filed as the gardener's own; null or absent otherwise. */
  new?: TrayNewPlant | null;
  /**
   * Where it shows most clearly in the photo, [ymin, xmin, ymax, xmax] on
   * 0-1000 as a seed packet's box is: its plant is filed with this part cut
   * out. Null or absent for none (the whole photo); moved with the photo when
   * it is turned or cropped.
   */
  box?: [number, number, number, number] | null;
}

/** What the model made of a photo. */
export interface TrayGuess {
  /** The catalogue plant it looks like, or null. */
  plant_id: number | null;
  /** The stage it looks to be at, or null. */
  stage_id: number | null;
  /** 0-100, or null when the model had nothing to say. */
  confidence: number | null;
  /** True once the model has been asked, whatever it answered. */
  done: boolean;
  /**
   * Whether the photo is of seed packets: true when the guess saw packets,
   * false when it did not (or the packet reader found none, or the gardener
   * said not), null before the guess.
   */
  packets?: boolean | null;
  /** What it shows in plain words ("zinnia"); null or absent when the guess did not say. */
  seen?: string | null;
  /** The same, plural ("zinnias"). */
  plural?: string | null;
  /**
   * The variety it is filed as: a flower Grow has no group for under the broad
   * parent guessed ("Zinnia" under Tender Annual Flower), or a plant tag's
   * ("Cherokee Purple"); null for the plant as Grow has it.
   */
  variety?: string | null;
  /** A photo of several kinds: each kind, two or more; empty or absent for one plant. */
  kinds?: TrayKind[];
  /** What kind of photo it is; 'one' or absent for one plant. A tray of one kind of seedling is 'seedlings' with no kinds. */
  group?: TrayGroup;
  /** The words on a plant tag in the photo; null or absent for none. */
  label?: string | null;
  /** A plant Grow does not have (plant_id is then null), filed as the gardener's own; null or absent otherwise. */
  new?: TrayNewPlant | null;
  /** Its botanical name ("Monarda"), for "Grow thinks this is …"; null or absent when the guess did not say. */
  latin?: string | null;
  /** Its family as a gardener says it ("mint"); null or absent when not said. */
  family?: string | null;
  /** The same in Latin ("Lamiaceae"); null or absent when not said. */
  familyLatin?: string | null;
}

/** A photo waiting in the tray, with its links. */
export interface TrayPhoto {
  /** The tray photo's id. */
  tray_id: number;
  /** Its stored name. */
  filename: string;
  /** 'YYYY-MM-DDTHH:MM:SS' on the phone's own clock, or null when the file had none. */
  taken_at: string | null;
  /** When it was put in the tray, as the server sends it. */
  uploaded_at: string;
  /** The bed the gardener said the batch is from, if they did. */
  location_id: number | null;
  /** Removed by the gardener: a space with Undo until its keep time runs out. */
  removed?: boolean;
  /** Where the gardener moved it: a tray id in another pile, 'own' for a pile of its own, null where Grow put it. */
  moved_to?: number | 'own' | null;
  /** What the model made of it (done false until asked). */
  guess: TrayGuess;
  /**
   * What the packet reader read off a photo of seed packets, as a scan to
   * confirm (with this photo's trayIds, so it leaves the tray once filed);
   * null until read, or when it is not packets.
   */
  packets?: PacketScan | null;
  /** Signed address of the full-size picture. */
  signedUrl: string;
  /** Signed address of its card copy. */
  cardUrl: string;
  /** Signed address of its thumbnail. */
  thumbUrl: string;
}

/** What the tray's list endpoints answer. */
interface TrayListResponse {
  /** The server's flag. */
  success: boolean;
  /** The photos asked about, or the whole tray. */
  photos: TrayPhoto[];
  /** The photos Grow filed onto plants by itself in this request (B160); absent from a server that does not. */
  filed?: FiledPhoto[];
}

/** What asking the server to understand photos gives back. */
export interface UnderstoodTray {
  /** The photos asked about, as the server now has them (the ones it filed are not here). */
  photos: TrayPhoto[];
  /** The photos Grow was sure of and filed onto their plants (B160); absent from a server that does not. */
  filed?: FiledPhoto[];
}

/**
 * What Grow's guess amounts to on a pile's card, as the server decided it
 * (services/tray/pilePick): the card starts here, and the gardener changes
 * what they like.
 */
export interface TrayPick {
  /** The type Grow guessed; null for nothing guessed or a plant Grow does not have. */
  plantId: number | null;
  /** Its crop (plant group). */
  cropId: number | null;
  /** The variety a new plant of it starts on: the one of the name Grow saw, else its "Not sure". */
  varietyId: number | null;
  /**
   * The variety name Grow saw (a flower's, "Zinnia"; a tag's, "Cherokee
   * Purple"), whether it was read off a tag, and each type of the crop that
   * has a variety of that name, with its id. Null when Grow saw none.
   */
  seen: { name: string; fromTag: boolean; named: Record<number, number> } | null;
  /** Bed id to the plant of that type growing there (the newest), picked when that bed is chosen. */
  growing: Record<number, number>;
  /** A plant Grow does not have, offered as the gardener's own. */
  newPlant: TrayNewPlant | null;
  /** What Grow thinks it is, said plainly while the guess is the pick; null otherwise. */
  thinks: PlantSuggestion | null;
}

/** A pile: photos probably of the same plant, asked about together, as the server grouped them. */
export interface TrayPile {
  /** Its key: its first photo's tray id as Grow grouped it; stable while it exists, and what a move targets. */
  key: number;
  /** Its photos, oldest first, removed ones included (the page leaves them out). */
  photoIds: number[];
  /** The first photo still in it (the page falls back to its first drawn photo when the lead was removed). */
  lead: number;
  /** "Sun 27 Sep", or a span; null when nothing is dated. */
  day: string | null;
  /** What it is taken to be: its surest photo's plant, its newest photo's stage (removed photos left out). */
  guess: TrayGuess;
  /**
   * B105: the server's words for a pile whose photos do not all look like its plant ("3 of 4 look like okra"), drawn in
   * place of one confident percent; null when they all agree.
   */
  agreeLine: string | null;
  /** A pile from an earlier day Grow thinks is the same plant: its key, its plant's name and day ("Jun 12"). */
  sameAs: { key: number; plant: string; day: string } | null;
  /** What Grow's guess amounts to on the card. */
  pick: TrayPick;
}

/**
 * A photo Grow was sure of and filed onto its plant by itself (B160). Everything on it is the server's: the sentence is its
 * todo_word row (tray.filed.to, "Filed to Sweet Horizon peas (Front bed)"), said in the gardener's language.
 */
export interface FiledPhoto {
  /** The tray photo it was. */
  trayId: number;
  /** The photo rows it became on the plant (a photo of one plant is one). */
  photoIds: number[];
  /** The plant it was filed onto. */
  plantObjId: number;
  /** That plant's full name. */
  plantName: string;
  /** Its bed's name, or null for a plant with no bed. */
  bedName: string | null;
  /** The server's sentence for it: "Filed to {plant} ({bed})". */
  line: string;
  /** A signed address for the photo's thumbnail; the line draws no picture without one. */
  thumbUrl?: string;
}

/** The tray: every photo, and its piles, newest added first. */
export interface Tray {
  /** Every photo on the tray, oldest first, removed ones included. */
  photos: TrayPhoto[];
  /** The piles. */
  piles: TrayPile[];
  /** The photos Grow filed onto plants by itself in the last photos.FILED_SHOW_HOURS, newest first (B160); absent from a server that does not file by itself. */
  filed?: FiledPhoto[];
}

/** What the tray's whole-tray endpoints answer. */
interface TrayResponse extends Tray {
  /** The server's flag. */
  success: boolean;
}

/**
 * The tray out of a whole-tray answer.
 *
 * @param data - The answer.
 * @returns Its photos and piles (none, where it had none) and the photos Grow filed by itself (absent where the server sent none).
 */
const trayOf = (data: TrayResponse): Tray => ({ photos: data.photos ?? [], piles: data.piles ?? [], filed: data.filed });

/**
 * Everything waiting in the tray, and its piles, their words in the app's language.
 *
 * @returns The tray.
 */
export async function getTray(): Promise<Tray> {
  const response = await apiClient.get<TrayResponse>('/tray', { params: { lang: getLanguage() } });
  return trayOf(response.data);
}

/** Where a photo is moved: a tray id in the pile it goes to (a pile's key), 'own', or null for back where Grow put it. */
export type TrayMoveTarget = number | 'own' | null;

/**
 * Moves photos between piles, or back where Grow put them (Undo). Kept on the
 * server, so a reload or a second phone shows the piles as they were left.
 *
 * @param moves - Each photo and where it goes.
 * @returns The tray as it now is.
 */
export async function moveInTray(moves: Array<{ trayId: number; to: TrayMoveTarget }>): Promise<Tray> {
  const response = await apiClient.post<TrayResponse>('/tray/move', { moves }, { params: { lang: getLanguage() } });
  return trayOf(response.data);
}

/**
 * Removes photos: each is gone from the page at once (no Undo, B45); the
 * server keeps it until its keep time runs out, and then deletes it. The originals stay on the phone or
 * in Google Photos.
 *
 * @param trayIds - The photos.
 * @returns The tray as it now is.
 */
export async function removeFromTray(trayIds: number[]): Promise<Tray> {
  const response = await apiClient.post<TrayResponse>('/tray/remove', { trayIds }, { params: { lang: getLanguage() } });
  return trayOf(response.data);
}

/**
 * The moment as the phone would say it, no zone: what the tray groups by.
 *
 * @param moment - When the photo was taken.
 * @returns "YYYY-MM-DDTHH:MM:SS".
 */
const asWallClock = (moment: DateTime): string => moment.toFormat("yyyy-LL-dd'T'HH:mm:ss");

/**
 * Puts one photo in the tray.
 *
 * @param file - Already shrunk by PhotoProcessor.
 * @param takenAt - From the file's EXIF, or its modified time; null when neither.
 * @param locationId - The bed the batch is from, if the gardener said.
 * @param copy - The photo's Google Photos item id where it has one, and keepCopy on Undo (B61).
 * @throws PhotoCopySkipped when the server says the photo is an exact copy of one already added (nothing was stored);
 *   its resend adds it after all.
 */
export async function addToTray(
  file: File,
  takenAt: DateTime | null,
  locationId: number | null,
  copy?: CopyOptions,
): Promise<TrayPhoto> {
  const formData = new FormData();
  formData.append('image', file);
  if (takenAt) formData.append('takenAt', asWallClock(takenAt));
  if (locationId !== null) formData.append('locationId', String(locationId));
  Object.entries(copyFields(copy)).forEach(([name, value]) => formData.append(name, value));
  const response = await apiClient.post<{ success: boolean; photo: TrayPhoto | null; duplicate_of?: unknown }>('/tray', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (isCopyAnswer(response.data) || !response.data.photo) {
    throw new PhotoCopySkipped(() => addToTray(file, takenAt, locationId, { ...copy, keepCopy: true }), readCopyPlace(response.data));
  }
  trackEvent('photos_added', { count: 1, where: 'tray' });
  return response.data.photo;
}

/**
 * Asks the server to understand these photos: its guess and, for a photo of seed packets, the packet reading, in one
 * request. Ones already understood come back unchanged; a packets photo the server left unread for the allowance is
 * read now if there is room.
 * Over the photo help's allowance it is refused with a 429 AI_LIMIT_* (its
 * date in the gardener's time zone, aiDateParams).
 *
 * @param trayIds - The photos.
 * @returns The photos as the server now has them and, from a server that files by itself (B160), the ones it filed.
 */
export async function understandTray(trayIds: number[]): Promise<UnderstoodTray> {
  const response = await apiClient.post<TrayListResponse>('/tray/guess', { trayIds }, { params: aiDateParams() });
  return { photos: response.data.photos, filed: response.data.filed };
}

/** What filing photos answers: the plant they went to, and how many. */
export interface SortResult {
  /** The server's flag. */
  success: boolean;
  /** The plant the photos went to (made, or already growing). */
  plant_obj_id: number;
  /** Its name, for the toast. */
  plant_name: string | null;
  /** How many photos went. */
  photos: number;
  /** The bed a new plant was made in (a new bed made from the photos too, D78); null or absent for one already growing. */
  location_id?: number | null;
}

/**
 * These photos are a new plant, of this kind, in this bed.
 *
 * `varietyId` is optional because the endpoint will choose one when it is
 * missing — the plant's default, or its first. The picker now asks, so it is
 * normally set; the server checks it belongs to the plant either way.
 *
 * `newVariety`, sent instead of a variety, names one of the gardener's own:
 * a flower Grow saw ("Zinnia") under its broad parent, or a plant tag's
 * "Cherokee Purple". The server uses the one Grow or they have by that name,
 * or makes it.
 *
 * `plantId` may instead be a plant Grow does not have ({ name, likePlantId }):
 * the server files it as the gardener's own plant, in the group of the plant
 * it grows most like, for Grow to review.
 *
 * Or a pile whose Type is "Not sure" (B52): { cropId, typeUnsure: true } is sent as the crop and the
 * flag, and the server files the plant on the crop's usual type, flagged; the app never picks that type.
 *
 * @param trayIds - The photos.
 * @param plantId - The plant (a catalogue type, or one of theirs), a plant Grow does not have, or a crop with Type "Not sure".
 * @param locationId - The bed: one of theirs, or a new one by name ({ newBedName }, D78 P4), which the server makes
 *   with the first photo as its cover (or finds, when they have a bed by that name).
 * @param stageId - The stage the photos show, or null when the gardener is not sure yet.
 * @param varietyId - The variety chosen; left out for the plant's own.
 * @param newVariety - A variety of their own by name, when no variety is chosen.
 */
export async function sortIntoNewPlant(
  trayIds: number[],
  plantId: number | NewPlantRequest | TypeUnsureRequest,
  locationId: number | NewBedRequest,
  stageId: number | null,
  varietyId?: number | null,
  newVariety?: string | null,
): Promise<SortResult> {
  const response = await apiClient.post<SortResult>('/tray/sort', {
    trayIds, stageId,
    // A bed of theirs, or a new one by name ("A new bed…" on the card, D78 P4), made with the first photo as its cover.
    ...(typeof locationId === 'number' ? { locationId } : { newBedName: locationId.newBedName }),
    ...(typeof plantId === 'number' ? { plantId }
      : 'typeUnsure' in plantId ? { cropId: plantId.cropId, typeUnsure: true }
        : { newPlant: plantId }),
    ...(newVariety ? { newVariety } : { varietyId: varietyId ?? undefined }),
  });
  return response.data;
}

/** A pile whose Type is "Not sure" (B52), as filing asks for it: the crop, and the flag. */
export interface TypeUnsureRequest {
  /** The crop (plant group) the plant is filed under; the server picks its usual type. */
  cropId: number;
  /** Always true: the type is not known. */
  typeUnsure: true;
}

/** A new bed by name, as filing asks for it ("A new bed…" on a card, D78 P4): the server makes it with the photo as its cover. */
export interface NewBedRequest {
  /** Its name, as typed. */
  newBedName: string;
}

/** A plant Grow does not have, as filing asks for it: its name, and the plant it grows most like. */
export interface NewPlantRequest {
  /** Its common name. */
  name: string;
  /** The catalogue plant it grows most like, whose group it is filed in. */
  likePlantId: number;
}

/**
 * One kind of a photo of several as it is filed: its plant (or a plant Grow
 * does not have, as the gardener's own), and a variety or a name for one of
 * the gardener's own.
 */
export interface KindItem {
  /** Its plant, a catalogue type or one of theirs; or newPlant instead; or cropId with typeUnsure. */
  plantId?: number;
  /** A plant Grow does not have, filed as the gardener's own, instead of plantId. */
  newPlant?: NewPlantRequest;
  /** Type "Not sure" from the row's change dialog (B52, D78): the crop, which the server files on its default type. */
  cropId?: number;
  /** Always true with cropId: the type is not known. */
  typeUnsure?: true;
  /** A variety there is; or newVariety, one of their own by name. */
  varietyId?: number | null;
  /** A variety of the gardener's own by name, instead of varietyId. */
  newVariety?: string | null;
  /** Its box, to file its own part of the photo; null or absent for the whole photo. */
  box?: [number, number, number, number] | null;
  /** Its own bed, one of theirs; or newBedName, a bed made for it by name. Neither: the bed given to fileKinds. */
  locationId?: number;
  /** A bed made for it by that name, instead of locationId. */
  newBedName?: string;
}

/**
 * Files a photo of several kinds as a plant for each kind kept, in one bed (or, for a kind that names its own bed, there):
 * each gets its own part of the photo (its box, cut out, with the whole photo
 * as the original it can be reset to) or the whole photo, dated as taken, at the stage that kind of photo
 * means (seedlings at a seedling stage, a bouquet in flower, a harvest at
 * harvesting, a bed at the stage seen), and the photo leaves the tray.
 * Answers as filing seed packets does (usePacketsFiled).
 *
 * @param trayId - The photo.
 * @param locationId - The bed for every kind that has none of its own: one of theirs, or a new one by name
 *   ({ newBedName }, D78 P4, a photo of a bed added from Beds), made with the photo as its cover.
 * @param kinds - The kinds kept.
 * @param group - What kind of photo it is.
 * @param upToRoom - On Free, add up to Free's room and leave the rest (result.left), rather than refuse them all.
 */
export async function fileKinds(trayId: number, locationId: number | NewBedRequest, kinds: KindItem[], group: TrayGroup, upToRoom = false): Promise<PacketResult> {
  const response = await apiClient.post<PacketResult>('/tray/kinds', {
    trayId, ...(typeof locationId === 'number' ? { locationId } : { newBedName: locationId.newBedName }), kinds, group,
    ...(upToRoom ? { upToRoom: true } : {}),
  });
  if (response.data.added > 0) trackEvent('plant_added', { how: 'photo', count: response.data.added });
  return response.data;
}

/**
 * These photos belong to a plant already growing.
 *
 * @param trayIds - The photos.
 * @param plantObjId - The plant they go to.
 * @param stageId - The stage the photos show, or null when the gardener is not sure.
 */
export async function sortIntoPlant(
  trayIds: number[],
  plantObjId: number,
  stageId: number | null,
): Promise<SortResult> {
  const response = await apiClient.post<SortResult>('/tray/sort', { trayIds, plantObjId, stageId });
  return response.data;
}

/**
 * The gardener says this photo is not seed packets: it comes back as a plant question.
 *
 * @param trayId - The photo.
 */
export async function notPackets(trayId: number): Promise<TrayPhoto> {
  const response = await apiClient.post<{ success: boolean; photo: TrayPhoto }>('/tray/not-packets', { trayId });
  return response.data.photo;
}

/**
 * Turns and crops a tray photo before it is filed. It comes back under a new
 * name and new links; a seed packet photo's packet boxes, and each kind's box
 * in a photo of several, move with it.
 *
 * @param trayId - The photo.
 * @param edit - The turn and crop (shared/lib/imageEdit).
 */
export async function editTrayImage(trayId: number, edit: ImageEdit): Promise<TrayPhoto> {
  const response = await apiClient.put<{ success: boolean; photo: TrayPhoto }>(`/tray/${trayId}/image`, edit);
  return response.data.photo;
}

/**
 * Puts a tray photo back as it was uploaded, undoing every turn and crop.
 *
 * @param trayId - The photo.
 */
export async function resetTrayImage(trayId: number): Promise<TrayPhoto> {
  const response = await apiClient.put<{ success: boolean; photo: TrayPhoto }>(`/tray/${trayId}/image/reset`);
  return response.data.photo;
}
